import { api } from "../http";
import type {
  OfflineCachePolicySettings,
  OfflineCacheSettings,
  OfflineCacheSettingsRequest,
} from "../types";

const ADMIN_PATH = "/api/admin/offline/cache-settings";
const POLICY_PATH = "/api/offline/cache-policy";
const TTL_FIELDS: Array<keyof OfflineCachePolicySettings> = [
  "defaultTtlHours",
  "referenceTtlHours",
  "documentTtlHours",
  "facilityTtlHours",
  "locationTtlHours",
  "metaTtlHours",
];

const clampHours = (value: unknown, fallback: number): number => {
  const parsed = Number.parseInt(String(value ?? ""), 10);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }
  return Math.max(1, Math.min(336, parsed));
};

const normalizePolicy = (input: unknown): OfflineCachePolicySettings => {
  const source = (input ?? {}) as Record<string, unknown>;
  const defaults: Record<keyof OfflineCachePolicySettings, number> = {
    defaultTtlHours: 12,
    referenceTtlHours: 24,
    documentTtlHours: 12,
    facilityTtlHours: 24,
    locationTtlHours: 24,
    metaTtlHours: 12,
  };
  return TTL_FIELDS.reduce<OfflineCachePolicySettings>(
    (acc, field) => {
      acc[field] = clampHours(source[field], acc[field]);
      return acc;
    },
    { ...defaults },
  );
};

const normalizeSettings = (input: unknown): OfflineCacheSettings => {
  const policy = normalizePolicy(input);
  const metadata = (input ?? {}) as Record<string, unknown>;
  return {
    ...policy,
    usingDefault: Boolean(metadata.usingDefault ?? metadata.default ?? false),
    updatedAt: typeof metadata.updatedAt === "string" ? metadata.updatedAt : null,
    updatedBy: typeof metadata.updatedBy === "string" ? metadata.updatedBy : null,
  };
};

const sanitizeRequest = (payload: OfflineCacheSettingsRequest): OfflineCacheSettingsRequest => {
  const next: OfflineCacheSettingsRequest = {};
  TTL_FIELDS.forEach((field) => {
    if (payload[field] === null) {
      next[field] = null;
      return;
    }
    if (typeof payload[field] === "number" && Number.isFinite(payload[field])) {
      const value = Math.max(1, Math.min(336, Math.trunc(payload[field] as number)));
      next[field] = value;
    }
  });
  return next;
};

export const offlineCachePolicyApi = {
  async getPolicy(): Promise<OfflineCachePolicySettings> {
    const response = await api.get<any>(POLICY_PATH);
    return normalizePolicy(response);
  },

  async getSettings(): Promise<OfflineCacheSettings> {
    const response = await api.get<any>(ADMIN_PATH);
    return normalizeSettings(response);
  },

  async updateSettings(payload: OfflineCacheSettingsRequest): Promise<OfflineCacheSettings> {
    const response = await api.put<any, OfflineCacheSettingsRequest>(ADMIN_PATH, sanitizeRequest(payload));
    return normalizeSettings(response);
  },
};
